import React, {
  RefObject,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import throttle from 'lodash/throttle'
import { SipContext } from './SipConn'

type State = {
  remoteRef: RefObject<HTMLDivElement>
  localRef: RefObject<HTMLDivElement>
}

export const VolumeMeterContext = React.createContext<State>({
  remoteRef: { current: null },
  localRef: { current: null },
})

const AudioContext = window.AudioContext || window['webkitAudioContext']

const listen = (
  stream: MediaStream,
  ref: RefObject<HTMLDivElement>,
  breakRef: React.MutableRefObject<boolean>,
  local: boolean
) => {
  let lastVal = 0

  const handleChange = (val: number) => {
    if (!ref.current) {
      return
    }

    ref.current.style.transform = `scale(${local ? val * 1.8 : val})`
  }

  const handleChangeThrottled = throttle(handleChange, 1000)

  let ctx = new AudioContext()

  const src = ctx.createMediaStreamSource(stream)
  const meter = createAudioMeter(ctx)
  src.connect(meter)

  drawLoop()

  function drawLoop() {
    const val = (() => {
      if (meter.volume < 0.05) {
        return 0
      } else if (meter.volume < 0.15) {
        return 1.2
      } else {
        return 1.35
      }
    })()

    if (val != lastVal) {
      lastVal = val
      handleChangeThrottled(val)
    }

    if (breakRef.current) {
      return
    }

    setTimeout(() => window.requestAnimationFrame(drawLoop), 100)
  }
}

const stop = (ref: RefObject<HTMLDivElement>) => {
  setTimeout(() => {
    if (ref.current) {
      ref.current.style.transform = ``
    }
  }, 2000)
}

type VolumeMeterProps = {
  children: React.ReactNode
}

const VolumeMeter: React.FC<VolumeMeterProps> = ({ children }) => {
  const { activeCall } = useContext(SipContext)
  const [active, setActive] = useState(false)
  const remoteRef = useRef<HTMLDivElement>(null)
  const localRef = useRef<HTMLDivElement>(null)

  const breakRef = useRef(false)

  useEffect(() => {
    breakRef.current = false

    if (
      activeCall &&
      activeCall.localMediaStream &&
      activeCall.remoteMediaStream &&
      !active
    ) {
      listen(activeCall.localMediaStream, localRef, breakRef, true)
      listen(activeCall.remoteMediaStream, remoteRef, breakRef, false)
      setActive(true)
    } else if (active) {
      stop(localRef)
      setActive(false)
      breakRef.current = true
    }
  }, [activeCall && activeCall.sessionState])

  return (
    <VolumeMeterContext.Provider
      value={{
        remoteRef,
        localRef,
      }}
    >
      {children}
    </VolumeMeterContext.Provider>
  )
}

// https://github.com/cwilso/volume-meter/blob/master/volume-meter.js

// powinno się przepisać na https://developer.mozilla.org/en-US/docs/Web/API/AudioWorkletNode
// bo createScriptProcessor jest deprecated od 2014

function createAudioMeter(
  audioContext,
  clipLevel = 0.98,
  averaging = 0.95,
  clipLag = 750
) {
  var processor = audioContext.createScriptProcessor(512)
  processor.onaudioprocess = volumeAudioProcess
  processor.clipping = false
  processor.lastClip = 0
  processor.volume = 0
  processor.clipLevel = clipLevel
  processor.averaging = averaging
  processor.clipLag = clipLag

  // this will have no effect, since we don't copy the input to the output,
  // but works around a current Chrome bug.
  processor.connect(audioContext.destination)

  processor.checkClipping = function () {
    if (!this.clipping) return false
    if (this.lastClip + this.clipLag < window.performance.now())
      this.clipping = false
    return this.clipping
  }

  processor.shutdown = function () {
    this.disconnect()
    this.onaudioprocess = null
  }

  return processor
}

function volumeAudioProcess(event) {
  var buf = event.inputBuffer.getChannelData(0)
  var bufLength = buf.length
  var sum = 0
  var x

  // Do a root-mean-square on the samples: sum up the squares...
  for (var i = 0; i < bufLength; i++) {
    x = buf[i]
    if (Math.abs(x) >= this.clipLevel) {
      this.clipping = true
      this.lastClip = window.performance.now()
    }
    sum += x * x
  }

  // ... then take the square root of the sum.
  var rms = Math.sqrt(sum / bufLength)

  // Now smooth this out with the averaging factor applied
  // to the previous sample - take the max here because we
  // want "fast attack, slow release."
  this.volume = Math.max(rms, this.volume * this.averaging)
}

export default VolumeMeter
